import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

import { ErrorState } from "~/components/ErrorState";
import { LoadingScreen } from "~/components/LoadingScreen";
import { ConnectAccount, ConnectAccountCredentials } from "~/components/onboarding";
import { accountBackfillMutationOptions, accountsListQueryOptions } from "~/lib/api";
import { isCredentialProvider } from "~/lib/connect-options";
import { track } from "~/lib/track";

export const Route = createFileRoute("/(authenticated)/verbind-account/")({
  validateSearch: (search: Record<string, unknown>) => ({
    provider: (search.provider as string) || undefined,
    reconnected: (search.reconnected as boolean) || undefined,
  }),
  component: VerbindAccount,
});

function VerbindAccount() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { provider, reconnected } = Route.useSearch();

  const { mutate: triggerBackfill } = useMutation(accountBackfillMutationOptions());
  const reconnectEnabled = import.meta.env.VITE_FEATURE_RECONNECT_ACCOUNT === "true";

  const completedRef = useRef(false);
  const handleSuccess = useCallback(() => {
    if (!completedRef.current) {
      completedRef.current = true;
      track("verbind_account_completed", { provider });
    }
    if (reconnectEnabled && provider) {
      triggerBackfill({ providerId: provider });
    }
    queryClient.invalidateQueries(accountsListQueryOptions());
    navigate({ to: "/dashboard", search: { reconnected: true } });
  }, [reconnectEnabled, provider, triggerBackfill, queryClient, navigate]);

  // The OAuth reconnect flow redirects back here with ?reconnected=true (no
  // onSuccess callback), so run the same success handler on arrival.
  useEffect(() => {
    if (reconnected === true && provider) {
      handleSuccess();
    }
  }, [reconnected, provider, handleSuccess]);

  // Returning from the OAuth reconnect redirect: show a loading state while the
  // effect triggers the backfill and navigates to the dashboard, so the connect
  // form never flashes.
  if (reconnected === true) {
    return <LoadingScreen />;
  }

  if (!provider) {
    return (
      <div className="bg-brand-white mx-4 mb-12 flex w-full max-w-2xl flex-col gap-6 p-12 lg:pb-10">
        <ErrorState
          title="Geen provider opgegeven"
          retryLabel="Terug naar dashboard"
          retryUrl="/dashboard"
        />
      </div>
    );
  }

  if (isCredentialProvider(provider)) {
    return (
      <ConnectAccountCredentials
        mode="reconnect"
        provider={provider}
        onSuccess={handleSuccess}
      />
    );
  }

  return <ConnectAccount mode="reconnect" provider={provider} />;
}
